import React from 'react';
import { Helmet } from 'react-helmet';
import get from 'lodash/get';

import { Urls } from 'src/appConstants';
import { Uid, EventDetailTicketType, EventPublicInfo } from 'src/types/dto';

const getDescription = (description: string) => {
  let descriptionText;
  try {
    const descriptionObj = JSON.parse(description ?? '{}');
    descriptionText = get(descriptionObj, 'ops[0].insert', '') || '';
  } catch (e) {
    descriptionText = description;
  }
  return descriptionText;
};

const getOffers = (id: Uid, ticketTypes: EventDetailTicketType[]) => {
  const ticketTypesAsOffers = ticketTypes.map(ticketType => {
    let availabilityType = 'InStock';
    if (ticketType.price <= 0) availabilityType = 'SoldOut';
    if (new Date(ticketType.sellEnd) < new Date()) availabilityType = 'SoldOut';
    return {
      url: `${Urls.BASE}/event/${id}`,
      availability: `https://schema.org/${availabilityType}`,
      price: ticketType.price,
      priceCurrency: 'BRL',
    };
  });

  return JSON.stringify(ticketTypesAsOffers);
};

const SEOEvent: React.FC<{ event?: EventPublicInfo }> = ({ event }) => {
  if (!event) {
    return null;
  }

  const addressAttrs = event.place.address.split('-') ?? [];
  const streetAddress = addressAttrs[0] && addressAttrs[0].trim();
  const addressLocality = addressAttrs[1] && addressAttrs[1].trim();
  const addressRegion = addressAttrs[2] && addressAttrs[2].trim();

  return (
    <Helmet>
      <title>{event.name} - Uticket</title>
      <script type="application/ld+json">
        {`
        {
            "@context": "https://schema.org",
            "@type": "Event",
            "url": "${Urls.BASE}/event/${event.id}",
            "name": "${event.name}",
            "description": "${getDescription(event.description)}",
            "startDate": "${event.schedules?.[0]?.start}",
            "endDate": "${event.schedules?.[0]?.end}",
            "location": {
                "@type": "Place",
                "name": "${event.place.name}",
                "address": {
                "@type": "PostalAddress",
                "streetAddress": "${streetAddress}",
                "addressLocality": "${addressLocality}",
                "addressRegion": "${addressRegion}",
                "addressCountry": "BR"
                }
            },
            "image": [
                "${Urls.IMG}/event/${event.id}/s",
                "${Urls.IMG}/event/${event.id}/m",
                "${Urls.IMG}/event/${event.id}/l"
            ],
            "offers": ${getOffers(event.id, event.ticketTypes)}
            }
        `}
      </script>
      {event?.hidden === true && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
      <meta property="og:title" content={event?.name} />
      <meta
        property="og:description"
        content={event?.description.slice(0, 250)}
      />
      <meta property="og:url" content={`${Urls.BASE}/event/${event?.id}`} />
      <meta property="og:image" content={`${Urls.IMG}/event/${event?.id}/`} />
      <meta property="og:image:alt" content="" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Uticket" />
    </Helmet>
  );
};

export default SEOEvent;
